/**
 * HiringPage Component
 * AIDLC Administrative Backend
 *
 * Two-section layout:
 *   1. Hiring Needs -- Submit and track hiring requests ("front door" to the pipeline)
 *   2. Hiring Pipeline -- Kanban-style board showing proposals through the lifecycle
 *
 * Flow: Submit Need -> Dan reviews -> Converts to HireProposal -> Pipeline takes over
 */

import { useState, useEffect, useCallback } from 'react';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import {
  getAllProposals,
  submitCeoDecision,
  updateProposalStatus,
  provisionAgent,
  getAllNeeds,
  createNeed,
  updateNeed,
  deleteNeed,
  type HireProposal,
  type HiringNeed,
} from '../../services/hiringService';

const STAGES = [
  { key: 'draft', label: 'Draft', color: 'neutral' },
  { key: 'hr_review', label: 'HR Review', color: 'info' },
  { key: 'director_review', label: 'Director Review', color: 'info' },
  { key: 'ceo_approval', label: 'CEO Approval', color: 'warning' },
  { key: 'approved', label: 'Approved', color: 'success' },
  { key: 'provisioning', label: 'Provisioning', color: 'info' },
  { key: 'active', label: 'Active', color: 'success' },
  { key: 'rejected', label: 'Rejected', color: 'danger' },
] as const;

const NEED_STATUS_CONFIG: Record<string, { label: string; variant: BadgeVariant }> = {
  submitted: { label: 'Submitted', variant: 'warning' },
  reviewing: { label: 'Under Review', variant: 'info' },
  approved: { label: 'Approved', variant: 'success' },
  converted: { label: 'Converted to Proposal', variant: 'success' },
  declined: { label: 'Declined', variant: 'danger' },
};

const PRIORITY_CONFIG: Record<string, { label: string; variant: BadgeVariant }> = {
  urgent: { label: 'Urgent', variant: 'danger' },
  high: { label: 'High', variant: 'warning' },
  normal: { label: 'Normal', variant: 'info' },
  low: { label: 'Low', variant: 'neutral' },
};

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';

function getStageVariant(status: string): BadgeVariant {
  const stage = STAGES.find((s) => s.key === status);
  return (stage?.color as BadgeVariant) || 'default';
}

function getStageLabel(status: string): string {
  const stage = STAGES.find((s) => s.key === status);
  return stage?.label || status;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function HiringPage() {
  const [activeTab, setActiveTab] = useState<'needs' | 'pipeline'>('needs');
  const [proposals, setProposals] = useState<HireProposal[]>([]);
  const [needs, setNeeds] = useState<HiringNeed[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<HireProposal | null>(null);
  const [ceoNotes, setCeoNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [view, setView] = useState<'pipeline' | 'list'>('pipeline');

  // Submit Need modal state
  const [showNeedModal, setShowNeedModal] = useState(false);
  const [needForm, setNeedForm] = useState({ title: '', role: '', reason: '', priority: 'normal' as string, notes: '' });
  const [isSubmittingNeed, setIsSubmittingNeed] = useState(false);

  // Need detail modal
  const [selectedNeed, setSelectedNeed] = useState<HiringNeed | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [proposalData, needData] = await Promise.all([
        getAllProposals(),
        getAllNeeds(),
      ]);
      setProposals(proposalData);
      setNeeds(needData);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Need handlers ---
  const handleSubmitNeed = async () => {
    if (!needForm.title.trim() || !needForm.role.trim() || !needForm.reason.trim()) return;
    setIsSubmittingNeed(true);
    try {
      const newNeed = await createNeed({
        title: needForm.title,
        role: needForm.role,
        reason: needForm.reason,
        priority: needForm.priority,
        notes: needForm.notes,
        requestedBy: 'CEO',
      });
      setNeeds((prev) => [newNeed, ...prev]);
      setShowNeedModal(false);
      setNeedForm({ title: '', role: '', reason: '', priority: 'normal', notes: '' });
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`);
    } finally {
      setIsSubmittingNeed(false);
    }
  };

  const handleUpdateNeedStatus = async (needId: string, status: string, extraFields?: Partial<HiringNeed>) => {
    try {
      const updated = await updateNeed(needId, { status, ...extraFields } as Partial<HiringNeed>);
      setNeeds((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      setSelectedNeed(updated);
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`);
    }
  };

  const handleDeleteNeed = async (needId: string) => {
    try {
      await deleteNeed(needId);
      setNeeds((prev) => prev.filter((n) => n.id !== needId));
      setSelectedNeed(null);
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`);
    }
  };

  // --- Proposal handlers ---
  const handleCeoDecision = async (decision: 'approved' | 'rejected') => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const updated = await submitCeoDecision(selected.id, decision, ceoNotes || undefined);
      setProposals((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setSelected(updated);
      setCeoNotes('');
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdvanceStatus = async (proposalId: string, newStatus: string) => {
    setActionLoading(true);
    try {
      const updated = await updateProposalStatus(proposalId, newStatus);
      setProposals((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setSelected(updated);
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleProvision = async (proposalId: string) => {
    setActionLoading(true);
    try {
      const result = await provisionAgent(proposalId);
      setProposals((prev) => prev.map((p) => (p.id === result.proposal.id ? result.proposal : p)));
      setSelected(result.proposal);
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading hiring pipeline..." />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-700 text-sm font-medium">Failed to load hiring pipeline</p>
        <p className="text-red-600 text-xs mt-1">{error}</p>
        <button onClick={fetchData} className="mt-3 px-4 py-2 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  const pendingNeeds = needs.filter((n) => n.status === 'submitted' || n.status === 'reviewing');

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-ldaja-black">Agent Hiring</h1>
          <p className="text-sm text-gray-500 mt-1">
            {pendingNeeds.length} pending need{pendingNeeds.length !== 1 ? 's' : ''} &middot; {proposals.length} proposal{proposals.length !== 1 ? 's' : ''} in pipeline
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowNeedModal(true)}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M12 4v16m8-8H4" />
            </svg>
            Submit Need
          </Button>
          <button
            onClick={fetchData}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50"
            title="Refresh"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('needs')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'needs'
              ? 'text-ldaja-blue border-ldaja-blue'
              : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Needs Queue
          {pendingNeeds.length > 0 && (
            <span className="ml-2 bg-ldaja-blue text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">
              {pendingNeeds.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('pipeline')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'pipeline'
              ? 'text-ldaja-blue border-ldaja-blue'
              : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Hiring Pipeline
          {proposals.length > 0 && (
            <span className="ml-2 bg-gray-200 text-gray-600 text-[10px] font-bold rounded-full px-1.5 py-0.5">
              {proposals.length}
            </span>
          )}
        </button>
      </div>

      {/* === NEEDS TAB === */}
      {activeTab === 'needs' && (
        <div>
          {needs.length === 0 ? (
            <EmptyState
              title="No hiring needs yet"
              description="Submit a need to start the hiring process. Dan will review the queue and work with the requester to build a full hire proposal."
            />
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Need</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Role</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Priority</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Requested By</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Assigned To</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Submitted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {needs.map((need) => (
                    <tr
                      key={need.id}
                      onClick={() => setSelectedNeed(need)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-ldaja-black">{need.title}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[250px]">{need.reason}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{need.role}</td>
                      <td className="px-4 py-3">
                        <Badge variant={PRIORITY_CONFIG[need.priority]?.variant || 'default'}>
                          {PRIORITY_CONFIG[need.priority]?.label || need.priority}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={NEED_STATUS_CONFIG[need.status]?.variant || 'default'} dot>
                          {NEED_STATUS_CONFIG[need.status]?.label || need.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{need.requestedBy}</td>
                      <td className="px-4 py-3 text-gray-600">{need.assignedTo || '--'}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(need.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* === PIPELINE TAB === */}
      {activeTab === 'pipeline' && (
        <PipelineView
          proposals={proposals}
          view={view}
          setView={setView}
          onSelectProposal={setSelected}
        />
      )}

      {/* Submit Need Modal */}
      <Modal
        isOpen={showNeedModal}
        onClose={() => setShowNeedModal(false)}
        title="Submit Hiring Need"
        size="md"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setShowNeedModal(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmitNeed}
              isLoading={isSubmittingNeed}
              disabled={!needForm.title.trim() || !needForm.role.trim() || !needForm.reason.trim()}
            >
              Submit Need
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Submit a hiring need and Dan will review the queue, work with you to flesh out the details, and create a full hire proposal.
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={needForm.title}
              onChange={(e) => setNeedForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              placeholder="e.g., Scrum Master Agent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
            <input
              type="text"
              value={needForm.role}
              onChange={(e) => setNeedForm((f) => ({ ...f, role: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              placeholder="e.g., Sprint Coordinator / Scrum Master"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Why is this hire needed?</label>
            <textarea
              value={needForm.reason}
              onChange={(e) => setNeedForm((f) => ({ ...f, reason: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              placeholder="Explain why this role is needed on the team..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={needForm.priority}
                onChange={(e) => setNeedForm((f) => ({ ...f, priority: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={needForm.notes}
              onChange={(e) => setNeedForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              placeholder="Any additional context for Dan..."
            />
          </div>
        </div>
      </Modal>

      {/* Need Detail Modal */}
      <Modal
        isOpen={!!selectedNeed}
        onClose={() => setSelectedNeed(null)}
        title={selectedNeed ? `Need: ${selectedNeed.title}` : ''}
        size="md"
        footer={
          selectedNeed && (
            <NeedActions
              need={selectedNeed}
              onUpdateStatus={handleUpdateNeedStatus}
              onDelete={handleDeleteNeed}
            />
          )
        }
      >
        {selectedNeed && <NeedDetail need={selectedNeed} />}
      </Modal>

      {/* Proposal Detail Modal */}
      <Modal
        isOpen={!!selected}
        onClose={() => { setSelected(null); setCeoNotes(''); }}
        title={selected ? `${selected.agentName} -- ${selected.role}` : ''}
        size="xl"
        footer={
          selected && (
            <ProposalActions
              proposal={selected}
              ceoNotes={ceoNotes}
              setCeoNotes={setCeoNotes}
              onCeoDecision={handleCeoDecision}
              onAdvanceStatus={handleAdvanceStatus}
              onProvision={handleProvision}
              loading={actionLoading}
            />
          )
        }
      >
        {selected && <ProposalDetail proposal={selected} />}
      </Modal>
    </div>
  );
}

/* ===== Pipeline View (existing Kanban + List) ===== */

function PipelineView({
  proposals,
  view,
  setView,
  onSelectProposal,
}: {
  proposals: HireProposal[];
  view: 'pipeline' | 'list';
  setView: (v: 'pipeline' | 'list') => void;
  onSelectProposal: (p: HireProposal) => void;
}) {
  const pipelineStages = ['draft', 'hr_review', 'director_review', 'ceo_approval', 'approved', 'provisioning', 'active', 'rejected'];
  const grouped: Record<string, HireProposal[]> = {};
  pipelineStages.forEach((s) => (grouped[s] = []));
  proposals.forEach((p) => {
    if (grouped[p.status]) grouped[p.status].push(p);
  });

  return (
    <div>
      {/* View toggle */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setView('pipeline')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            view === 'pipeline' ? 'bg-ldaja-blue text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          Kanban
        </button>
        <button
          onClick={() => setView('list')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            view === 'list' ? 'bg-ldaja-blue text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          List
        </button>
      </div>

      {proposals.length === 0 ? (
        <EmptyState
          title="No hiring proposals yet"
          description="Approved needs get converted into proposals that flow through this pipeline."
        />
      ) : view === 'pipeline' ? (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {pipelineStages.map((stageKey) => {
            const stage = STAGES.find((s) => s.key === stageKey)!;
            const items = grouped[stageKey] || [];
            return (
              <div key={stageKey} className="flex-shrink-0 w-64">
                <div className="bg-gray-50 rounded-lg border border-gray-200">
                  <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700">{stage.label}</span>
                    <span className="text-xs text-gray-400 bg-gray-200 rounded-full px-2 py-0.5">{items.length}</span>
                  </div>
                  <div className="p-2 space-y-2 min-h-[100px]">
                    {items.map((proposal) => (
                      <ProposalCard key={proposal.id} proposal={proposal} onClick={() => onSelectProposal(proposal)} />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Agent</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">HR Score</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Proposed By</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {proposals.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => onSelectProposal(p)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        style={{ background: p.avatarColor }}
                      >
                        {p.agentName.charAt(0)}
                      </div>
                      <div>
                        <div className="font-medium text-ldaja-black">{p.agentName}</div>
                        <div className="text-xs text-gray-500">{p.agentId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{p.role}</td>
                  <td className="px-4 py-3">
                    <Badge variant={getStageVariant(p.status)} dot>{getStageLabel(p.status)}</Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {p.hrReview.totalScore !== null ? `${p.hrReview.totalScore}/25` : '--'}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{p.proposedBy}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(p.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ===== Need Detail ===== */

function NeedDetail({ need }: { need: HiringNeed }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-ldaja-blue/10 flex items-center justify-center">
          <svg className="w-5 h-5 text-ldaja-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-ldaja-black">{need.title}</h3>
          <p className="text-xs text-gray-500">{need.role}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Badge variant={PRIORITY_CONFIG[need.priority]?.variant || 'default'}>
            {PRIORITY_CONFIG[need.priority]?.label || need.priority}
          </Badge>
          <Badge variant={NEED_STATUS_CONFIG[need.status]?.variant || 'default'} dot>
            {NEED_STATUS_CONFIG[need.status]?.label || need.status}
          </Badge>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Reason for Hire</h4>
        <p className="text-sm text-gray-700">{need.reason}</p>
      </div>

      {need.notes && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Notes</h4>
          <p className="text-sm text-gray-700">{need.notes}</p>
        </div>
      )}

      {need.assignedTo && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Assigned to:</span>
          <span className="font-medium text-ldaja-black">{need.assignedTo}</span>
        </div>
      )}

      {need.proposalId && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Linked proposal:</span>
          <span className="font-medium text-ldaja-blue">{need.proposalId}</span>
        </div>
      )}

      <div className="flex items-center gap-6 text-xs text-gray-400 pt-2 border-t border-gray-100">
        <span>Requested by: {need.requestedBy}</span>
        <span>Submitted: {formatDate(need.createdAt)}</span>
        <span>Updated: {formatDate(need.updatedAt)}</span>
        <span>ID: {need.id}</span>
      </div>
    </div>
  );
}

function NeedActions({
  need,
  onUpdateStatus,
  onDelete,
}: {
  need: HiringNeed;
  onUpdateStatus: (id: string, status: string, extraFields?: Partial<HiringNeed>) => void;
  onDelete: (id: string) => void;
}) {
  const retriggerBtn = need.assignedTo ? (
    <Button variant="secondary" size="sm" onClick={() => onUpdateStatus(need.id, need.status, { assignedTo: need.assignedTo })}>
      Re-trigger {need.assignedTo}
    </Button>
  ) : null;

  const unassignBtn = need.assignedTo ? (
    <Button variant="secondary" size="sm" onClick={() => onUpdateStatus(need.id, need.status, { assignedTo: null })}>
      Unassign
    </Button>
  ) : null;

  switch (need.status) {
    case 'submitted':
      return (
        <div className="flex items-center gap-2">
          <Button variant="danger" size="sm" onClick={() => onDelete(need.id)}>Delete</Button>
          <Button size="sm" onClick={() => onUpdateStatus(need.id, 'reviewing', { assignedTo: 'dan' })}>
            Assign to Dan for Review
          </Button>
        </div>
      );
    case 'reviewing':
      return (
        <div className="flex items-center gap-2">
          <Button variant="danger" size="sm" onClick={() => onUpdateStatus(need.id, 'declined')}>Decline</Button>
          <Button size="sm" onClick={() => onUpdateStatus(need.id, 'approved')}>
            Approve Need
          </Button>
          {retriggerBtn}
          {unassignBtn}
        </div>
      );
    case 'approved':
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm text-green-600 font-medium">Approved -- Dan will create a hire proposal</span>
          {retriggerBtn}
          {unassignBtn}
        </div>
      );
    case 'converted':
      return <span className="text-sm text-green-600 font-medium">Converted to hire proposal</span>;
    case 'declined':
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm text-red-600 font-medium">Need was declined</span>
          <Button variant="secondary" size="sm" onClick={() => onUpdateStatus(need.id, 'submitted', { assignedTo: null })}>
            Reopen
          </Button>
        </div>
      );
    default:
      return null;
  }
}

/* ===== Proposal Sub-Components ===== */

function ProposalCard({ proposal, onClick }: { proposal: HireProposal; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-md p-3 cursor-pointer hover:shadow-sm hover:border-gray-300 transition-all"
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
          style={{ background: proposal.avatarColor }}
        >
          {proposal.agentName.charAt(0)}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-ldaja-black truncate">{proposal.agentName}</div>
          <div className="text-xs text-gray-500 truncate">{proposal.role}</div>
        </div>
      </div>
      {proposal.hrReview.totalScore !== null && (
        <div className="flex items-center gap-1 mt-1">
          <span className="text-xs text-gray-400">HR:</span>
          <span className={`text-xs font-medium ${proposal.hrReview.totalScore >= 18 ? 'text-green-600' : proposal.hrReview.totalScore >= 13 ? 'text-yellow-600' : 'text-red-600'}`}>
            {proposal.hrReview.totalScore}/25
          </span>
          {proposal.hrReview.recommendation && (
            <span className="text-xs text-gray-400 ml-1">({proposal.hrReview.recommendation})</span>
          )}
        </div>
      )}
      <div className="text-xs text-gray-400 mt-1">by {proposal.proposedBy}</div>
    </div>
  );
}

function ProposalDetail({ proposal }: { proposal: HireProposal }) {
  const [showInstruction, setShowInstruction] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
          style={{ background: proposal.avatarColor }}
        >
          {proposal.agentName.charAt(0)}
        </div>
        <div>
          <h3 className="text-lg font-semibold text-ldaja-black">{proposal.fullName}</h3>
          <p className="text-sm text-gray-500">{proposal.role}</p>
          {proposal.catchphrase && (
            <p className="text-sm text-gray-400 italic mt-1">"{proposal.catchphrase}"</p>
          )}
          <div className="flex items-center gap-3 mt-2">
            <Badge variant={getStageVariant(proposal.status)} dot>{getStageLabel(proposal.status)}</Badge>
            <span className="text-xs text-gray-400">{proposal.yearsExp} yrs exp</span>
            <span className="text-xs text-gray-400">Proposed by: {proposal.proposedBy}</span>
          </div>
        </div>
      </div>

      {proposal.expertise.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Expertise</h4>
          <div className="flex flex-wrap gap-1.5">
            {proposal.expertise.map((e) => (
              <span key={e} className="text-xs bg-blue-50 text-ldaja-blue px-2 py-1 rounded-md">{e}</span>
            ))}
          </div>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">HR Review (Harper Wells)</h4>
        {proposal.hrReview.scores ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(proposal.hrReview.scores).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between bg-white rounded px-3 py-1.5">
                  <span className="text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                  <ScoreBar score={val as number} max={5} />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200">
              <div>
                <span className="text-xs text-gray-500">Total Score:</span>
                <span className={`ml-2 text-sm font-bold ${(proposal.hrReview.totalScore ?? 0) >= 18 ? 'text-green-600' : (proposal.hrReview.totalScore ?? 0) >= 13 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {proposal.hrReview.totalScore}/25
                </span>
              </div>
              <div>
                <span className="text-xs text-gray-500">Recommendation:</span>
                <span className="ml-2 text-sm font-medium text-gray-700 capitalize">{proposal.hrReview.recommendation}</span>
              </div>
            </div>
            {proposal.hrReview.notes && (
              <p className="text-sm text-gray-600 mt-2 bg-white rounded p-2">{proposal.hrReview.notes}</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400">Pending HR review</p>
        )}
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Director Review (Dan)</h4>
        {proposal.directorReview.recommendation ? (
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Recommendation:</span>
              <Badge variant={proposal.directorReview.recommendation === 'hire' ? 'success' : proposal.directorReview.recommendation === 'no_hire' ? 'danger' : 'warning'}>
                {proposal.directorReview.recommendation}
              </Badge>
            </div>
            {proposal.directorReview.notes && (
              <p className="text-sm text-gray-600 mt-2 bg-white rounded p-2">{proposal.directorReview.notes}</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400">Pending director review</p>
        )}
      </div>

      {proposal.ceoDecision.decision && (
        <div className={`rounded-lg p-4 ${proposal.ceoDecision.decision === 'approved' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <h4 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: proposal.ceoDecision.decision === 'approved' ? '#15803d' : '#b91c1c' }}>
            CEO Decision
          </h4>
          <Badge variant={proposal.ceoDecision.decision === 'approved' ? 'success' : 'danger'} dot>
            {proposal.ceoDecision.decision === 'approved' ? 'Approved' : 'Rejected'}
          </Badge>
          {proposal.ceoDecision.notes && (
            <p className="text-sm mt-2" style={{ color: proposal.ceoDecision.decision === 'approved' ? '#166534' : '#991b1b' }}>
              {proposal.ceoDecision.notes}
            </p>
          )}
          {proposal.ceoDecision.decidedAt && (
            <p className="text-xs mt-1 opacity-60">{formatDate(proposal.ceoDecision.decidedAt)}</p>
          )}
        </div>
      )}

      {proposal.instructionContent && (
        <div>
          <button
            onClick={() => setShowInstruction(!showInstruction)}
            className="text-xs font-medium text-ldaja-blue hover:underline"
          >
            {showInstruction ? 'Hide' : 'Show'} Instruction File (.md)
          </button>
          {showInstruction && (
            <pre className="mt-2 p-3 bg-gray-900 text-green-400 rounded-lg text-xs overflow-auto max-h-64 font-mono">
              {proposal.instructionContent}
            </pre>
          )}
        </div>
      )}

      <div className="flex items-center gap-6 text-xs text-gray-400 pt-2 border-t border-gray-100">
        <span>Created: {formatDate(proposal.createdAt)}</span>
        <span>Updated: {formatDate(proposal.updatedAt)}</span>
        <span>ID: {proposal.id}</span>
      </div>
    </div>
  );
}

function ScoreBar({ score, max }: { score: number; max: number }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: max }, (_, i) => (
        <div
          key={i}
          className={`w-3 h-3 rounded-sm ${i < score ? 'bg-ldaja-blue' : 'bg-gray-200'}`}
        />
      ))}
      <span className="text-xs font-medium text-gray-700 ml-1">{score}</span>
    </div>
  );
}

function ProposalActions({
  proposal,
  ceoNotes,
  setCeoNotes,
  onCeoDecision,
  onAdvanceStatus,
  onProvision,
  loading,
}: {
  proposal: HireProposal;
  ceoNotes: string;
  setCeoNotes: (v: string) => void;
  onCeoDecision: (decision: 'approved' | 'rejected') => void;
  onAdvanceStatus: (id: string, status: string) => void;
  onProvision: (id: string) => void;
  loading: boolean;
}) {
  switch (proposal.status) {
    case 'draft':
      return (
        <Button size="sm" onClick={() => onAdvanceStatus(proposal.id, 'hr_review')} isLoading={loading}>
          Send to HR Review
        </Button>
      );
    case 'hr_review':
      return (
        <Button size="sm" onClick={() => onAdvanceStatus(proposal.id, 'director_review')} isLoading={loading}>
          Advance to Director Review
        </Button>
      );
    case 'director_review':
      return (
        <Button size="sm" onClick={() => onAdvanceStatus(proposal.id, 'ceo_approval')} isLoading={loading}>
          Send to CEO for Approval
        </Button>
      );
    case 'ceo_approval':
      return (
        <div className="flex items-center gap-3 w-full">
          <input
            type="text"
            placeholder="CEO notes (optional)..."
            value={ceoNotes}
            onChange={(e) => setCeoNotes(e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
          />
          <Button variant="danger" size="sm" onClick={() => onCeoDecision('rejected')} isLoading={loading}>
            Reject
          </Button>
          <Button size="sm" onClick={() => onCeoDecision('approved')} isLoading={loading}>
            Approve Hire
          </Button>
        </div>
      );
    case 'approved':
      return (
        <Button size="sm" onClick={() => onAdvanceStatus(proposal.id, 'provisioning')} isLoading={loading}>
          Begin Provisioning
        </Button>
      );
    case 'provisioning':
      return (
        <Button size="sm" onClick={() => onProvision(proposal.id)} isLoading={loading}>
          Provision Agent Now
        </Button>
      );
    case 'active':
      return <span className="text-sm text-green-600 font-medium">Agent is live and operational</span>;
    case 'rejected':
      return <span className="text-sm text-red-600 font-medium">Proposal was rejected</span>;
    default:
      return null;
  }
}
